<template><div><h1 id="artikelbewegungen" tabindex="-1"><a class="header-anchor" href="#artikelbewegungen"><span>Artikelbewegungen</span></a></h1>
<p>Artikelbewegungen — Zu- und Abgänge von Artikelmengen aus Partien</p>
<h2 id="beschreibung" tabindex="-1"><a class="header-anchor" href="#beschreibung"><span>Beschreibung</span></a></h2>
<p>Unter Artikelbewegungen werden Zu- bzw. Abgänge von Artikelmengen aus <RouteLink to="/referenz/refs/ft.refdata.lagpartien.html" title="Partien">Partien</RouteLink> verstanden. Artikelbewegungen beinhalten auch Bewegungen von Artikeln ohne physische Lagerbestände (fiktive und assemblierte Artikel). Sie sind entstanden aus den früheren Partientypen A, I, K, L und wurden für Umlagerungen um einige neue Typen ergänzt.</p>
<p>Bewegungen beziehen sich immer auf eine bestimmte Partie, was auch Umlagerungen innerhalb eines Lagers möglich macht (z.B. bei Belieferung eines Zweitlagers ohne Verwendung der Auftragsabwicklung).</p>
<h3 id="kumulation-verkaufsbewegungen" tabindex="-1"><a class="header-anchor" href="#kumulation-verkaufsbewegungen"><span>Kumulation Verkaufsbewegungen</span></a></h3>
<p>Um die Datenmengen übersichtlich zu halten, werden Verkaufsbewegungen kumuliert gespeichert (Ausnahme: Retouren werden einzeln gespeichert). Die Kumulation erfolgt pro Partie und Datum, Auftragsart, Medium, Teil assemblierten Artikels, Verkaufspreis, Rabatt. Alle übrigen Artikelbewegungen werden nach dem Bruttoprinzip verbucht, d.h. dass alle Stornobuchungen ebenfalls ausgewiesen werden.</p>
<p>Pfad: <code v-pre>Logistik → Auswertungen → Artikelbewegungen</code></p>
<h2 id="datenfelder" tabindex="-1"><a class="header-anchor" href="#datenfelder"><span>Datenfelder</span></a></h2>
<p>Die folgenden Typen von Artikelbewegungen sind implementiert:</p>
<table>
<thead>
<tr>
<th>Typ</th>
<th>Beschreibung</th>
</tr>
</thead>
<tbody>
<tr>
<td>A</td>
<td>Abschreibung</td>
</tr>
<tr>
<td>AS</td>
<td>Storno Abschreibung</td>
</tr>
<tr>
<td>I</td>
<td>Inventurkorrektur</td>
</tr>
<tr>
<td>K</td>
<td>Korrekturbuchungen manuell</td>
</tr>
<tr>
<td>L</td>
<td>Lieferungen / Rücklieferungen von / an Lieferanten</td>
</tr>
<tr>
<td>LS</td>
<td>Storno Lieferung / Rücklieferung</td>
</tr>
<tr>
<td>S</td>
<td>Stücklisten-Umlagerungen (für Normalartikel mit Stückliste)</td>
</tr>
<tr>
<td>U</td>
<td>Umlagerungen</td>
</tr>
<tr>
<td>W</td>
<td>Warenverkauf (Verkäufe an Kunden)</td>
</tr>
<tr>
<td>WS</td>
<td>Retouren von Kunden</td>
</tr>
</tbody>
</table>
<div class="hint-container tip">
<p class="hint-container-title">Anmerkung</p>
<p>Retoure aus Auftrag geht je nach Parametrisierung nicht direkt ans Auslieferlager, sondern an ein Retourenlager
::: |</p>
<p>Nur die Bewegungstypen A, K und L können direkt durch den Anwender erfasst werden, die übrigen werden bei der Fakturierung, der Inventur etc. durch das System automatisch gebildet. Parametrisiert wird dies in der <RouteLink to="/manual/administration/systemverwaltung.html" title="7. Systemverwaltung">Systemtabelle Artikelbewegungen</RouteLink></p>
<dl>
<dt>Bt</dt>
<dd>Bewegungstyp, Bezeichnung Bewegungstyp</dd>
<dt>BewDatum</dt>
<dd>Datum der Artikelbewegung; &quot;Valutadatum&quot;, zu dem eine Bewegung im System eingebucht wird. Standardvorschlag = Tagesdatum</dd>
<dt>Nr</dt>
<dd>Anzeige der Laufnummer der Artikelbewegung, wird vom System automatisch vergeben</dd>
<dt>PNr</dt>
<dd>Partiennummer, welche bei der Artikelbewegung bebucht wird</dd>
<dt>Lager</dt>
<dd>Lager-Personen-ID, welche bei der Artikelbewegung bebucht wird</dd>
<dt>LBez.</dt>
<dd>Anzeige Lagerbezeichnung aus Tabelle &quot;Lagerorte&quot;</dd>
<dt>Bemerkung</dt>
<dd>Freies Textfeld, 19 Zeichen, alphanumerisch</dd>
<dt>Menge</dt>
<dd>Anzahl Artikel, welche bewegt werden; bei Verkaufsbewegungen wird pro Tag kumuliert ein Wert ausgewiesen; alle übrigen Bewegungen werden einzeln erfasst und angezeigt</dd>
<dt>Erf.</dt>
<dd>Erfasser der Bewegung</dd>
<dt>Erf.dat.</dt>
<dd>Erfassungsdatum der Artikelbewegung</dd>
<dt>Medium</dt>
<dd>Medium, das im Auftrag erfasst wurde (nur bei Warenverkäufen)</dd>
<dt>EK-Pr/Einh</dt>
<dd>Einkaufspreis pro Einheit, Standardvorschlag aus Artikelstamm, kann manuell geändert werden</dd>
<dt>Zus.K/Einh</dt>
<dd>Zusatzkosten (nur bei einer Lieferung, kann manuell geändert werden)</dd>
<dt>Brutto-VP</dt>
<dd>Verkaufspreis, Information aus Artikelstamm oder aus Auftragsposition, je nach Bewegungstyp</dd>
<dt>Rabatt</dt>
<dd>Rabatt (nur bei Bewegungstypen W, WS)</dd>
<dt>A-Art</dt>
<dd>Auftragsart (nur bei Bewegungstypen W, WS)</dd>
</dl>
<h2 id="funktionen" tabindex="-1"><a class="header-anchor" href="#funktionen"><span>Funktionen</span></a></h2>
<dl>
<dt>Filter</dt>
<dd>Ein-, Ausschalten des Artikelfilters</dd>
<dt>erfassen</dt>
<dd>Erfassen von manuellen Artikelbewegungen. Möglich sind die Bewegungstypen <span class="guilabel">L</span>, <span class="guilabel">K</span>, <span class="guilabel">A</span>.<br>Wird durch eine manuelle Buchung der Lagerbestand verkleinert und ist am betroffenen Lager eine gewisse Menge des Artikels bereits in Auftragspositionen reserviert, dann kommt die folgende Meldung:<pre class="screen">ArtikelNr. 01180G In angegebenem Lagerort sind nur 0 Stück des Artikels
32 reservierte Artikel auf vorreserviert zurücksetzen?</pre>Soll die Buchung durchgeführt werden, dann muss hier mit <span class="guilabel">ja</span> geantwortet werden und das System setzt auf gewissen Auftragspositionen den Artikel auf <span class="quote">„<span class="quote">vorreserviert</span>“</span>. Dabei wird nach Auftragsalter vorgegangen, d.h. alte Auftragspositionen behalten nach Möglcihkeit den Status <span class="guilabel">3</span> (reserviert).</dd>
<dt>Storno</dt>
<dd>Stornieren einer Bewegung. Storni sind nur möglich für die manuell erfassbaren Bewegungstypen</dd>
</dl>
<h2 id="siehe-auch" tabindex="-1"><a class="header-anchor" href="#siehe-auch"><span>Siehe auch</span></a></h2>
<p><RouteLink to="/manual/trade/artikel_und_lagerverwaltung.html" title="3. Artikel- und Lagerverwaltung">Lagerverwaltung</RouteLink></p>
</div>
</div></template>


